import moment from 'moment';

import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get, post } from 'utils/api';
import { logError } from 'utils/log';
import { downloadIndividualFile, waitUntilZipIsReadyAndGetPresignedURL } from 'utils/file';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { __t, applyTranslations } from 'utils/language';

import type { Action, ContentListCollectionData, ContentListStats, Dispatch, List, ThunkAction } from 'types';

applyTranslations(require('./content-list-reducers.i18n.json'));

const { CONTENT_LISTS } = API.END_POINTS;

const LOADING = 'contentList/LOADING';
const LOADED = 'contentList/LOADED';
const RESET = 'contentList/RESET';
const RESET_DATA = 'contentList/RESET_DATA';
const ATTACHMENTS_LOADING = 'contentList/ATTACHMENTS_LOADING';

type State = {
  content: Array<ContentListCollectionData>,
  hasMorePages: boolean,
  stats: ContentListStats,
  isLoading: boolean
};

const initialState = {
  content: [],
  stats: {
    creatorsCount: 0,
    assetsCount: 0
  },
  hasMorePages: true,
  isLoading: false,
  isAttachmentsLoading: false
};

const ITEMS_PER_PAGE = 6;

const contentLoaded = (content: Array<List>, hasMorePages: boolean, stats: ContentListStats): Action => ({
  type: LOADED,

  payload: {
    content,
    hasMorePages,
    stats
  }
});

const loadingStateChanged = (isLoading: boolean): Action => ({
  type: LOADING,

  payload: {
    isLoading
  }
});

const resetContent = (): Action => ({
  type: RESET_DATA
});

export const fullResetContentList = (): Action => ({
  type: RESET
});

const attachementLoadingStateChanged = (isAttachmentsLoading: boolean): Action => ({
  type: ATTACHMENTS_LOADING,

  payload: {
    isAttachmentsLoading
  }
});

export function downloadAttachments(organizationId: number, listId: string, assetsIds: Array<string>): ThunkAction {
  const date = moment().format('YYYY-MM-DD');
  const time = moment().format('HH-MM');
  const filename = `Collabary - All high-resolution content pieces - ${date} at ${time}.zip`;

  return async (dispatch: Dispatch) => {
    dispatch(attachementLoadingStateChanged(true));

    try {
      const url = CONTENT_LISTS.getSmartListAttachments(organizationId, listId, filename);
      const response = await post(url, {
        data: { assets: assetsIds }
      }, { suppressGlobalNotification: true });
      const zipFileS3Url = response?.data.url;
      const jsonFileS3Url = response?.data.statusUrl;

      if (zipFileS3Url && jsonFileS3Url) {
        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Returned an empty response');
      }

      dispatch(attachementLoadingStateChanged(false));
    } catch (e) {
      logError(e);
      dispatch(attachementLoadingStateChanged(false));
    }
  };
}

function genericFetchContent(page: number, url: string): ThunkAction {
  return async (dispatch: Dispatch) => {
    dispatch(loadingStateChanged(true));

    if (page === 1) {
      dispatch(resetContent());
    }

    try {
      const { data } = await get(url, {}, { suppressGlobalNotification: true });
      const lists = data.data || [];
      const meta = data.meta || {};
      const stats = {
        creatorsCount: data.creatorsCount,
        assetsCount: data.assetsCount
      };

      dispatch(contentLoaded(lists, meta.currentPage < meta.pagesTotal, stats));
    } catch (e) {
      logError(e);
      dispatch(danger(__t('content_list_reducers.content_list_loading_error')));
    } finally {
      dispatch(loadingStateChanged(false));
    }
  };
}

export const fetchSmartContentList = (organizationId: number, listId: string, page: number, sortBy: string): ThunkAction => {
  const params = snakeCaseKeys({
    sortBy,
    page,
    itemsPerPage: ITEMS_PER_PAGE
  });
  const queryString = Querystring.objectToString(params);
  const url = `${CONTENT_LISTS.getSmartList(organizationId, listId)}?${queryString}`;

  return genericFetchContent(page, url);
};

export default createReducer<State, any>('contentList', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RESET_DATA]: (state) => ({
    ...state,
    content: [],
    stats: {
      creatorsCount: 0,
      assetsCount: 0
    }
  }),
  [LOADING]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [LOADED]: (state, { content, hasMorePages, stats }) => ({
    ...state,
    hasMorePages,
    stats,
    content: state.content.concat(content)
  }),
  [ATTACHMENTS_LOADING]: (state, { isAttachmentsLoading }) => ({
    ...state,
    isAttachmentsLoading
  })
});
